import {
  isGiacenzaColumnKey,
  LISTA_SPEDIZIONI_COLUMNS,
  ListaSpedizioneColumn
} from '../_models/spedizioni/lista-spedizioni.columns';

// Larghezze CSS Grid per la tabella Excel-like della Lista Spedizioni.
// minmax(min, max): il `min` evita che la colonna collassi sotto la soglia di
// leggibilità, il `max` (fr o px) consente l'espansione su display larghi.
// La somma dei `min` può superare la larghezza del container su laptop 13"/tablet:
// in quel caso il wrapper genitore deve essere `overflow-x-auto`.
// Le etichette sono esplicite ("Qtà Residua Ordine Cliente") e l'intestazione va a capo su
// due righe: i `min` delle quantita' sono dimensionati perche' ci stiano senza troncare.
export const LISTA_SPEDIZIONI_GRID_WIDTHS: Record<string, string> = {
  CodArt:           'minmax(130px, 1.2fr)',
  DesArt:           'minmax(180px, 1.8fr)',
  Cliente:          'minmax(160px, 1.4fr)',
  Stato:            'minmax(100px, 130px)',
  DataSpedizione:   'minmax(100px, 120px)',
  DataConsegnaRich: 'minmax(110px, 130px)',
  NumDocOriginale:  'minmax(120px, 1fr)',
  UnitaMisura:      'minmax(80px, 100px)',
  QtaOrdUmDoc:      'minmax(120px, 140px)',
  QtOrdImpUmDoc:    'minmax(120px, 140px)',
  NumRegistraz:     'minmax(140px, 170px)',
  NumProgrRiga:     'minmax(80px, 100px)',
  CodArtCliFor:     'minmax(130px, 1fr)',
  DatiSpedNum:        'minmax(90px, 110px)',
  DatiSpedLocalita1:  'minmax(140px, 1.2fr)',
  ValoreCampo2:       'minmax(140px, 1.4fr)',
  PrioritaOrdine:     'minmax(90px, 110px)',
  DataConsegnaConf:   'minmax(110px, 130px)',
  RigaSollecitata:    'minmax(100px, 120px)',
  SerieNumApp:        'minmax(160px, 1.6fr)',
  QtaConf:            'minmax(120px, 140px)',
  PrelevatoImb:       'minmax(120px, 140px)',
  NotaInternaPers:    'minmax(160px, 1.6fr)',
  NotaInternaOsSpedp: 'minmax(160px, 1.6fr)',
  RagSoc:             'minmax(160px, 1.6fr)',
  VarianteArt:        'minmax(100px, 1fr)',
  TipoAnagrafica:     'minmax(110px, 130px)',
  IdDocOrd:           'minmax(90px, 110px)',
  IdDoc:              'minmax(100px, 120px)',
  DataDocumento:      'minmax(110px, 130px)',
  QtInSped:           'minmax(120px, 140px)',
  QtCons:             'minmax(120px, 140px)',
  QtaResOrv:          'minmax(120px, 140px)',
};

export const LISTA_SPEDIZIONI_ACTIONS_COL_WIDTH = '56px';

const COLUMN_LABEL_MAP = new Map(LISTA_SPEDIZIONI_COLUMNS.map(c => [c.key, c.label]));

// ── Catalogo esteso (feature 118, issue #3354) ────────────────────────────────────────────────
//
// COLUMN_LABEL_MAP e' una costante di MODULO costruita dal solo catalogo statico: le colonne
// giacenza esistono solo a runtime, quindi le funzioni che ne dipendono devono poter ricevere
// anche le colonne dinamiche. Senza, `sanitize` scarterebbe ogni chiave `Giac:*` come orfana e le
// intestazioni mostrerebbero la chiave grezza `Giac:GLT|M01` al posto della descrizione — in
// entrambi i casi senza alcun errore, con il solo sintomo "la configurazione non funziona".
//
// `dynamicColumns` assente ⇒ comportamento identico a prima della feature.
//
// ── Memoizzazione (feature 119, FR-027, research D23) ────────────────────────────────────────
//
// La mappa e' MEMOIZZATA sull'identita' dell'array ricevuto: cache di un solo elemento,
// ricostruita solo quando il riferimento cambia. Senza, ogni chiamata costruirebbe una Map di
// 35+ voci via spread — costo tollerabile finche' lo pagavano le sole intestazioni desktop
// (~15 chiamate per ciclo di change detection), insostenibile con la card guidata dai dati,
// dove diventa UNA chiamata per pill per riga (20 righe × 10 pill = 200 mappe per ciclo, sul
// dispositivo piu' lento dei due). `OnPush` limita quanti cicli avvengono, non quanto costa il
// singolo ciclo.
//
// La cache di un solo elemento basta perche' `giacenzaColumns` e' ricostruito solo al
// caricamento delle voci e al cambio organizzazione, e scende per `@Input()` come STESSO
// riferimento a tutte le righe (research D14).
//
// La mappa e' RICOSTRUITA da zero a ogni cambio di riferimento, mai aggiornata per fusione: al
// ritorno a un catalogo gia' visto (A → B → A) una fusione lascerebbe le etichette di B sulle
// giacenze di A — nessun errore a video, nessuna build rossa, solo dati sbagliati letti come
// giusti. Il caso e' reale (`reloadForOrganizzazione` supporta il cambio avanti e indietro) ed
// e' coperto da `lista-spedizioni-grid.helper.spec.ts`.
interface DynamicColumnIndex {
  labels: Map<string, string>;
  columns: Map<string, ListaSpedizioneColumn>;
}

const EMPTY_DYNAMIC_INDEX: DynamicColumnIndex = {
  labels: COLUMN_LABEL_MAP,
  columns: new Map<string, ListaSpedizioneColumn>(),
};

let cachedDynamicColumns: readonly ListaSpedizioneColumn[] | undefined;
let cachedDynamicIndex: DynamicColumnIndex = EMPTY_DYNAMIC_INDEX;

function dynamicIndexWith(dynamicColumns?: readonly ListaSpedizioneColumn[]): DynamicColumnIndex {
  if (!dynamicColumns || dynamicColumns.length === 0) {
    return EMPTY_DYNAMIC_INDEX;
  }
  if (dynamicColumns === cachedDynamicColumns) {
    return cachedDynamicIndex;
  }
  cachedDynamicColumns = dynamicColumns;
  cachedDynamicIndex = {
    labels: new Map([...COLUMN_LABEL_MAP, ...dynamicColumns.map(c => [c.key, c.label] as [string, string])]),
    columns: new Map(dynamicColumns.map(c => [c.key, c] as [string, ListaSpedizioneColumn])),
  };
  return cachedDynamicIndex;
}

function labelMapWith(dynamicColumns?: readonly ListaSpedizioneColumn[]): Map<string, string> {
  return dynamicIndexWith(dynamicColumns).labels;
}

// Colonna dinamica corrispondente alla chiave, se e' una voce di giacenza del catalogo
// ricevuto. Lookup in tempo costante sulla stessa cache delle etichette (FR-027).
export function getListaSpedizioniGiacenzaColumn(
  key: string,
  dynamicColumns?: readonly ListaSpedizioneColumn[]): ListaSpedizioneColumn | undefined {
  return dynamicIndexWith(dynamicColumns).columns.get(key);
}

// Larghezza di una colonna giacenza: come le altre quantita, allineata a destra e tabellare.
const GIACENZA_GRID_WIDTH = 'minmax(100px, 120px)';

export function buildListaSpedizioniGridTemplate(visibleColumns: readonly string[]): string {
  const cols = (visibleColumns ?? [])
    .map(c => columnWidth(c))
    .join(' ');
  return `${cols} ${LISTA_SPEDIZIONI_ACTIONS_COL_WIDTH}`;
}

function columnWidth(key: string): string {
  if (isGiacenzaColumnKey(key)) {
    return GIACENZA_GRID_WIDTH;
  }
  return LISTA_SPEDIZIONI_GRID_WIDTHS[key] ?? 'minmax(100px, 1fr)';
}

export function getListaSpedizioniColumnLabel(key: string,
                                              dynamicColumns?: readonly ListaSpedizioneColumn[]): string {
  return labelMapWith(dynamicColumns).get(key) ?? key;
}

// Suggerimento al passaggio del mouse di un'intestazione o di una pill (feature 119, FR-009).
// Per una colonna di giacenza riporta descrizione E codice — li' la ripetizione non occupa
// spazio, quindi la forma e' una sola anche quando l'etichetta e' gia' il ripiego
// `Giacenza {code}`. Per le altre colonne e' l'etichetta.
export function getListaSpedizioniColumnTooltip(key: string,
                                                dynamicColumns?: readonly ListaSpedizioneColumn[]): string {
  const label = getListaSpedizioniColumnLabel(key, dynamicColumns);
  const code = getListaSpedizioniGiacenzaColumn(key, dynamicColumns)?.code;
  return code ? `${label} — ${code}` : label;
}

// Scarta le chiavi non più presenti nel catalogo (preferenze salvate prima di una
// rimozione di colonna): una chiave orfana genererebbe una traccia di griglia
// senza cella corrispondente e disallineerebbe tutte le colonne successive.
// Le preferenze sono persistite per utente e non sono migrabili, quindi il filtro
// vale anche per ogni rimozione futura.
export function sanitizeListaSpedizioniColumns(keys: readonly string[],
                                              dynamicColumns?: readonly ListaSpedizioneColumn[]): string[] {
  const labels = labelMapWith(dynamicColumns);
  return (keys ?? []).filter(k => labels.has(k));
}

// Variante per le colonne di raggruppamento: i token portano il suffisso di direzione
// (`Cliente<` / `Cliente>`), che va tolto prima del confronto con il catalogo e
// riattaccato al token superstite.
export function sanitizeListaSpedizioniGroupColumns(tokens: readonly string[]): string[] {
  return (tokens ?? []).filter(t => COLUMN_LABEL_MAP.has(stripDirection(t)));
}

function stripDirection(token: string): string {
  const last = token[token.length - 1];
  return (last === '<' || last === '>') ? token.slice(0, -1) : token;
}
